import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createSidebarItem,
  deleteSidebarItem,
  getSidebarItems,
  mediaUrl,
  updateSidebarItem,
  uploadMedia,
} from '../api';
import Button from './ui/Button';
import FileButton from './ui/FileButton';

// Initial values for the sidebar item form
// Anfangswerte für das Sidebar-Element-Formular
const initialFormState = {
  title: '',
  icon_url: '',
  position: 1,
  tags: '',
  excludedTags: ''
}

function AdminSidebarForm() {
  const { t } = useTranslation();

  const [items, setItems] = useState([]);
  const [form, setForm] = useState(initialFormState);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [iconFile, setIconFile] = useState(null);
  const iconInputRef = useRef(null);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    loadSidebarItems();
  }, [])

  async function loadSidebarItems() {
    try {
      setIsLoading(true);
      setError('');

      const data = await getSidebarItems();

      setItems(data);
    } catch (error) {
      console.error('Faild to load sidebar:', error);

      setError(error.message || t('adminPanel.failedLoadSidebar'))
    } finally {
      setIsLoading(false);
    }
  }

  function updateField(field) {
    return (event) => {
      setForm((current) => ({
        ...current,
        [field]: event.target.value
      }));

      setError('');
      setSuccess('');
    }
  }

  function handleIconChange(event) {
    const file = event.target.files?.[0] || null;

    setIconFile(file);
    setError('');
    setSuccess('');
  }


  async function handleSubmit(event) {
    event.preventDefault();

    const title = form.title.trim();

    if (!title) {
      setError(t('adminPanel.titleRequired'));
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');
      setSuccess('');

      let icon_url = form.icon_url;

      if (iconFile) {
        const uploaded = await uploadMedia(iconFile, 'interface');
        // A path either way: the server returns one, and an absolute URL from
        // an older server is reduced to its path against the page.
        icon_url = new URL(uploaded.url, window.location.origin).pathname;
      }

      const sidebarData = {
        title,
        icon_url,
        position: Number(form.position),
        tags: form.tags
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean),
        excludedTags: form.excludedTags
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean)
      };

      if (editingId !== null) {

        const updatedItem = await updateSidebarItem(
          editingId,
          sidebarData
        );

        setItems((current) =>
          current.map((item) =>
            item.sidebar_item_id === editingId
              ? updatedItem
              : item
          )
        );

        // Notify other components that the sidebar has changed
        // Andere Komponenten darüber informieren, dass sich die Sidebar geändert hat
        window.dispatchEvent(new Event('sidebar-updated'));

        setSuccess(t('adminPanel.sidebarUpdated'));
      } else {

        if (!iconFile) {
          setError(t('adminPanel.iconRequired'));
          return;
        }

        const newItem = await createSidebarItem(sidebarData);

        setItems((current) => [...current, newItem]);

        // Notify other components that a new sidebar item was created
        // Andere Komponenten darüber informieren, dass ein neues Sidebar-Element erstellt wurde
        window.dispatchEvent(new Event('sidebar-updated'));

        setSuccess(t('adminPanel.sidebarCreated'));
      }

      setForm(initialFormState);
      setIconFile(null);
      setEditingId(null);

      if (iconInputRef.current) {
        iconInputRef.current.value = '';
      }

    } catch (error) {
      console.error(
        editingId !== null
          ? 'Failed to update sidebar item:'
          : 'Failed to create sidebar item:',
        error
      );

      if (error?.data?.code === 'POSITION_ALREADY_EXISTS') {
        setError(t('adminPanel.positionAlreadyExists'));
      } else {
        setError(
          error.message ||
          (
            editingId !== null
              ? t('adminPanel.failedUpdateSidebar')
              : t('adminPanel.failedCreateSidebar')
          )
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleEdit(item) {
    setEditingId(item.sidebar_item_id);

    setForm({
      title: item.title || '',
      icon_url: item.icon_url || '',
      position: item.position || 1,
      tags: item.tags?.join(', ') || '',
      excludedTags: item.excludedTags?.join(', ') || ''
    });

    setIconFile(null);

    if (iconInputRef.current) {
      iconInputRef.current.value = '';
    }

    setError('');
    setSuccess('');
  }

  async function handleDelete(id) {
    try {
      setError('');
      setSuccess('');

      await deleteSidebarItem(id);

      setItems((current) =>
        current.filter(
          (item) => item.sidebar_item_id !== id
        ));

      // Notify the sidebar menu that an item was deleted
      // Das Sidebar-Menü darüber informieren, dass ein Element gelöscht wurde
      window.dispatchEvent(new Event('sidebar-updated'));

      setSuccess(t('adminPanel.sidebarDeleted'))
    } catch (error) {
      console.error('Failed to delete sidebar item:', error)

      setError(error.message || t('adminPanel.failedLoadSidebar'));
    }
  }

  return (
    <div>
      {error && (
        <div className="login-message login-message-error" role="alert">
          <i className="fa-solid fa-circle-exclamation" />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="login-message login-message-success" role="status" >
          <i className="fa-solid fa-circle-check" />
          <span>{success}</span>
        </div>
      )}

      <form className="account-form"
        onSubmit={handleSubmit}
        noValidate>

        <div className="admin-user-row-body">

          <div className="account-field-row">
            <label htmlFor="sidebarIconUrl">
              {t('adminPanel.sidebarIcon')}
            </label>
            <div>
              <FileButton
                inputRef={iconInputRef}
                id="sidebarIconUrl"
                name="icon"
                variant="secondary"
                accept="image/*"
                disabled={isSubmitting}
                onChange={handleIconChange}
              >
                <i className="fa-solid fa-upload" />
                {t('adminPanel.chooseIcon')}
              </FileButton>

              {iconFile && (
                <span className="admin-sidebar-file-name">
                  {iconFile.name}
                </span>
              )}
            </div>
          </div>

          <div className="admin-sidebar-form-row">
            <div className="account-field-row">
              <label htmlFor="sidebarTitle">
                {t('adminPanel.sidebarTitle')}
              </label>
              <input id="sidebarTitle" name="title" type="text"
                value={form.title}
                disabled={isSubmitting}
                onChange={updateField('title')} />
            </div>
          </div>

          <div className="account-field-row">
            <label htmlFor="sidebarTags">
              {t('adminPanel.sidebarTags')}
            </label>
            <input id="sidebarTags" name="tags" type="text"
              value={form.tags}
              disabled={isSubmitting}
              onChange={updateField('tags')}
            />
          </div>

          <div className="account-field-row">
            <label htmlFor="sidebarExcludedTags">
              {t('adminPanel.sidebarExcludedTags')}
            </label>
            <input id="sidebarExcludedTags" name="excludedTags" type="text"
              value={form.excludedTags}
              disabled={isSubmitting}
              onChange={updateField('excludedTags')}
            />
          </div>

          <div className="account-field-row">
            <label htmlFor="sidebarPosition">
              {t('adminPanel.sidebarPosition')}
            </label>
            <input
              id="sidebarPosition"
              name="position"
              type="number"
              min="1"
              value={form.position}
              disabled={isSubmitting}
              onChange={updateField('position')} />
          </div>

          <div className="account-actions">

            <Button
              variant="primary"
              type="submit"
              disabled={isSubmitting}
            >
              <i
                className={
                  editingId !== null
                    ? 'fa-solid fa-floppy-disk'
                    : 'fa-solid fa-plus'
                }
              />

              {isSubmitting
                ? editingId !== null
                  ? t('adminPanel.updating')
                  : t('adminPanel.creating')
                : editingId !== null
                  ? t('adminPanel.update')
                  : t('adminPanel.create')
              }
            </Button>
            {editingId !== null && (
              <Button
                variant="secondary"
                onClick={() => {
                  setEditingId(null);
                  setForm(initialFormState);
                  setIconFile(null);

                  if (iconInputRef.current) {
                    iconInputRef.current.value = '';
                  }

                  setError('');
                  setSuccess('');
                }}
              >
                {t('adminPanel.cancel')}
              </Button>
            )}
          </div>

          <div className="account-field-row">
            <h5>{t('adminPanel.sidebarList')}</h5>
          </div>
          <div className="admin-sidebar-list">
            {isLoading ? (
              <p>{t('adminPanel.sidebarLoading')}</p>
            ) : items.length === 0 ? (
              <p>{t('adminPanel.noSidebarItems')}</p>
            ) : (
              items.map((item) => (
                <div
                  className="admin-sidebar-item"
                  key={item.sidebar_item_id}
                >
                  <div className="admin-sidebar-item-icon">
                    {item.icon_url && (
                      <img
                        src={mediaUrl(item.icon_url)}
                        alt={item.title}
                      />
                    )}
                  </div>

                  <div className="admin-sidebar-item-title">
                    <strong>{item.title}</strong>
                  </div>

                  <div className="admin-sidebar-item-tags">
                    {item.tags?.length
                      ? item.tags.map((tag) => `#${tag}`).join(', ')
                      : '—'}
                  </div>

                  <div className="admin-sidebar-item-excluded">
                    {item.excludedTags?.length
                      ? item.excludedTags.map((tag) => `#${tag}`).join(', ')
                      : '—'}
                  </div>

                  <div className="admin-sidebar-item-position">
                    {item.position}
                  </div>

                  <div className="admin-sidebar-item-actions">
                    <Button
                      variant="primary"
                      onClick={() => handleEdit(item)}
                      aria-label={t('adminPanel.editSidebarItem')}
                      title={t('adminPanel.editSidebarItem')}
                    >
                      <i className="fa-solid fa-pen-to-square" />
                    </Button>
                    <Button
                      variant="primary"
                      className="admin-sidebar-delete"
                      onClick={() => handleDelete(item.sidebar_item_id)}
                      aria-label={t('adminPanel.deleteSidebarItem')}
                      title={t('adminPanel.deleteSidebarItem')}
                    >
                      <i className="fa-solid fa-trash" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </form>



    </div>
  );
}

export default AdminSidebarForm;
